import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { getCategoryById, getServicesByCategory, applyFilters, defaultFilters, type FilterState } from '@/lib/content';
import ServiceCard from '@/components/base/ServiceCard';
import FilterBar from '@/components/base/FilterBar';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import CategorySidebar from '@/components/feature/CategorySidebar';

const PAGE_SIZE = 6;

export default function CategoryList() {
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = getCategoryById(categoryId);

  const initialSort = (searchParams.get('sort') as FilterState['sort']) || 'latest';
  const [filters, setFilters] = useState<FilterState>({ ...defaultFilters, sort: initialSort });

  useSeo({
    title: category ? `${category.name} - 在线服务平台分类` : '服务分类 - 在线服务平台',
    description: category
      ? `浏览${category.name}相关在线服务信息，快速查找所需资源。`
      : '浏览在线平台服务分类信息，快速查找所需资源。',
    keywords: category ? `${category.name},在线服务,${category.name}在线资源` : '在线服务分类',
  });

  const list = useMemo(
    () => applyFilters(getServicesByCategory(categoryId), filters),
    [categoryId, filters],
  );

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = list.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleFilterChange = (next: FilterState) => {
    setFilters(next);
    setPage(1);
    if (next.sort !== 'latest') {
      setSearchParams({ sort: next.sort });
    } else {
      setSearchParams({});
    }
  };

  const handleReset = () => {
    setFilters({ ...defaultFilters });
    setPage(1);
    setSearchParams({});
  };

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-folder-warning-line"
          title="分类不存在或已下线"
          description="该服务分类可能已被移除，返回全部分类看看其他内容吧。"
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/categories" className="hover:text-primary-600 transition-colors">全部分类</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{category.name}</span>
      </nav>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className="w-full lg:w-60 shrink-0">
          <CategorySidebar activeId={category.id} />
        </div>

        <div className="flex-1 min-w-0">
          <header className="mb-6">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-2xl">
                <i className={category.icon}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </header>

          <FilterBar
            filters={filters}
            onChange={handleFilterChange}
            onReset={handleReset}
            resultCount={list.length}
          />

          <div className="mt-6">
            {pageItems.length === 0 ? (
              <EmptyState
                icon="ri-file-search-line"
                title="暂无符合条件的服务"
                description="当前筛选条件下没有找到内容，换一组条件或重置筛选试试。"
                actionText="重置筛选"
                onAction={handleReset}
              />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5" data-product-shop>
                  {pageItems.map((item) => (
                    <ServiceCard key={item.id} service={item} />
                  ))}
                </div>
                <Pagination current={safePage} total={totalPages} onChange={setPage} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}